<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: [Number, String],
    default: 0,
  },
  triggerRules: {
    type: Object,
    default: () => ({}),
  },
  templateParams: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['edit', 'delete', 'view', 'pause', 'resume', 'cancel']);

const { t } = useI18n();

const STATUS_COMPLETED = 'completed';
const STATUS_PROCESSING = 'processing';
const STATUS_PAUSED = 'paused';
const STATUS_ACTIVE = 'active';

const { formatMessage } = useMessageFormatter();

const checkpointTag = computed(() => {
  const rules = props.triggerRules || {};
  const params = props.templateParams || {};
  const custom = rules.checkpoint_label || params.checkpoint_label;
  if (custom && custom.trim()) return custom.trim();
  if (props.title) {
    const slug = props.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return `campanha-whats-${slug}`;
  }
  return null;
});

const statusTextColor = computed(() => ({
  'text-teal-600 dark:text-teal-400 font-semibold': props.status === STATUS_PROCESSING || props.status === STATUS_ACTIVE || props.isEnabled,
  'text-amber-600 dark:text-amber-400 font-semibold': props.status === STATUS_PAUSED,
  'text-emerald-600 dark:text-emerald-400 font-semibold': props.status === STATUS_COMPLETED,
  'text-n-slate-11': !props.status || props.status === 'scheduled',
}));

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (props.status === STATUS_COMPLETED) {
    return 'Concluída';
  }

  if (props.status === STATUS_PROCESSING) {
    return 'Em Execução';
  }

  if (props.status === STATUS_ACTIVE) {
    return 'Ativa / Agendada';
  }

  if (props.status === STATUS_PAUSED) {
    return 'Pausada';
  }

  return 'Agendada';
});

const inboxName = computed(() => props.inbox?.name || 'WhatsApp');

const inboxIcon = computed(() => {
  if (!props.inbox) return 'i-ri-whatsapp-fill';
  const {
    medium,
    channel_type: type,
    voice_enabled: voiceEnabled,
  } = props.inbox;
  if (type === 'Channel::Api' || type === 'Channel::Whatsapp' || type === 'api' || type === 'whatsapp') {
    return 'i-ri-whatsapp-fill';
  }
  return getInboxIconByType(type, medium, 'fill', voiceEnabled);
});
</script>

<template>
  <CardLayout layout="row">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex items-center justify-between gap-3 w-fit flex-wrap">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2.5 py-0.5 rounded-full bg-n-alpha-2 border border-n-weak"
          :class="statusTextColor"
        >
          <span
            v-if="status === STATUS_PROCESSING || status === STATUS_ACTIVE"
            class="size-1.5 rounded-full bg-teal-500 animate-ping mr-1.5"
          />
          <span
            v-else-if="status === STATUS_PAUSED"
            class="size-1.5 rounded-full bg-amber-500 mr-1.5"
          />
          <span
            v-else-if="status === STATUS_COMPLETED"
            class="size-1.5 rounded-full bg-emerald-500 mr-1.5"
          />
          {{ campaignStatus }}
        </span>

        <!-- Tag Dinâmica de Checkpoint Visível no Card -->
        <span
          v-if="!isLiveChatType && checkpointTag"
          class="text-[11px] font-mono px-2 py-0.5 rounded-md bg-blue-500/10 text-[#2B8EFF] dark:text-[#52A3FF] border border-blue-500/20"
        >
          🏷️ {{ checkpointTag }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>
    </div>
    <div class="flex items-center justify-end gap-1.5 flex-shrink-0">
      <!-- Ver Relatório / Mini Dashboard (para WhatsApp e campanhas one-off) -->
      <Button
        v-if="!isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-bar-chart-2"
        v-tooltip.top="'Ver Detalhes & Progresso'"
        @click="emit('view')"
      />

      <!-- Botão Pausar Disparo -->
      <Button
        v-if="!isLiveChatType && (status === STATUS_PROCESSING || status === STATUS_ACTIVE)"
        variant="faded"
        size="sm"
        color="amber"
        icon="i-lucide-pause"
        v-tooltip.top="'Pausar Disparo'"
        @click="emit('pause')"
      />

      <!-- Botão Retomar Disparo -->
      <Button
        v-if="!isLiveChatType && status === STATUS_PAUSED"
        variant="faded"
        size="sm"
        color="teal"
        icon="i-lucide-play"
        v-tooltip.top="'Retomar Disparo'"
        @click="emit('resume')"
      />

      <!-- Botão Editar -->
      <Button
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-vertical"
        v-tooltip.top="'Editar Campanha'"
        @click="emit('edit')"
      />

      <!-- Botão Excluir -->
      <Button
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        v-tooltip.top="'Excluir Campanha'"
        @click="emit('delete')"
      />
    </div>
  </CardLayout>
</template>